import { useEffect, useState } from 'react';
import { z } from 'zod';
import { HOST_SESSION_KEY, StoredCredentials, loadHostCredentials, saveCredentials } from '../session/credentials.ts';
import { Link, useNavigate } from '../shell/router.tsx';

const HostAccess = z.object({
  sessionId: z.string().min(1),
  participant: StoredCredentials.extend({ isHost: z.literal(true) }),
});

function decodeAccess(value: string): unknown {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
  const bytes = Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

export function HostEntry() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fragment = window.location.hash;
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);

    const access = new URLSearchParams(fragment.startsWith('#') ? fragment.slice(1) : fragment).get('access');
    if (access) {
      try {
        const parsed = HostAccess.parse(decodeAccess(access));
        saveCredentials(parsed.sessionId, parsed.participant);
        localStorage.setItem(HOST_SESSION_KEY, parsed.sessionId);
        navigate(`/s/${encodeURIComponent(parsed.sessionId)}?setup=1`, { replace: true });
        return;
      } catch {
        setError('This host access link is invalid or incomplete. Ask the session host for a fresh private link.');
        return;
      }
    }

    try {
      const sessionId = localStorage.getItem(HOST_SESSION_KEY);
      const credentials = sessionId ? loadHostCredentials(sessionId) : null;
      if (sessionId && credentials?.isHost) {
        navigate(`/s/${encodeURIComponent(sessionId)}?setup=1`, { replace: true });
        return;
      }
    } catch {
      // Storage may be unavailable. The friendly recovery message below still applies.
    }
    setError('This browser has not been set up as your host device. Open your saved Momentum Host launcher to restore access. The guest invitation does not include host controls.');
  }, [navigate]);

  return (
    <main className="page stack">
      <h1>Host access</h1>
      {error ? <div className="banner banner-danger" role="alert"><p>{error}</p></div> : <p role="status">Opening host controls…</p>}
      {error ? <p><Link className="button" to="/momentum">Start a fresh demo as host</Link></p> : null}
    </main>
  );
}
